import { z } from 'zod';
import { useGT } from 'gt-react';
import { Action, Subject } from '@erp/authz';
import {
  workbookSamplingSchema,
  workbookValidationSchema,
  type WorkbookDefinition,
  type WorkbookRun,
} from '@erp/shared-types';
import { rpcClient } from '@/api/rpc/client';
import { toast } from '@/components/ui/toast';
import { useCan } from '@/hooks/useCan';
import { useFrontendTool } from '@/hooks/useFrontendTool';
import type { WorkbookAgentPort } from './useWorkbookAgentTools';
import { applyAgentEdit } from './useWorkbookAgentWriteTools';
import {
  agentSampleRun,
  checkSampleAssertion,
  sampleAssertionRead,
  sampleAssertionSchema,
  sampleRunRefusal,
  type SampleAssertion,
} from './workbookAgentSampling';
import { withValidation } from './worksheetValidations';

/** How long run_workbook_sample waits for its Run, and how often it looks. */
const SAMPLE_RUN_WAIT_MS = 180_000;
const SAMPLE_RUN_POLL_MS = 1_000;
/** Rows of each Worksheet the sample run returns, at most. */
const MAX_SAMPLE_ROWS = 20;
/** Rows one pinned example reads. */
const ASSERTION_ROWS = 20;

async function completedRun(
  workbookId: string,
  runId: string,
): Promise<WorkbookRun | undefined> {
  const deadline = Date.now() + SAMPLE_RUN_WAIT_MS;
  while (Date.now() < deadline) {
    const run = (await rpcClient.workbooks.runs({ workbookId })).find(
      (item) => item.id === runId,
    );
    if (run && (run.status === 'completed' || run.status === 'failed'))
      return run;
    await new Promise((resolve) => setTimeout(resolve, SAMPLE_RUN_POLL_MS));
  }
  return undefined;
}

/** One pinned example checked against a finished Run on sample's rows. */
async function checkAssertion(
  workbookId: string,
  run: WorkbookRun,
  definition: WorkbookDefinition,
  assertion: SampleAssertion,
) {
  const read = sampleAssertionRead(assertion, definition, run);
  if (!('sheetId' in read)) return read;
  const rows = await rpcClient.workbooks.resultRows({
    workbookId,
    runId: run.id,
    sheetId: read.sheetId,
    limit: ASSERTION_ROWS,
    filters: read.filters,
  });
  return checkSampleAssertion(
    assertion,
    rows.columns,
    rows.kind === 'rows' ? rows.rows : [],
    rows.rowCount,
  );
}

/**
 * The on-page agent's fast loop: Run on sample, waited for, with bounded
 * results, its Representativeness report, drift and throwaway pinned
 * examples; and relational checks it may only propose to the user.
 */
export function useWorkbookAgentSampleTools(
  workbookId: string,
  port: WorkbookAgentPort,
) {
  const gt = useGT();
  const [canUpdate] = useCan(Action.Update, Subject.Workbook);
  const [canExecute] = useCan(Action.Execute, Subject.Workbook);
  const sampleTool = useFrontendTool({
    name: 'run_workbook_sample',
    enabled: canExecute,
    description:
      "Save the workbook and calculate every worksheet over a small, join-coherent sample of its data, waiting for it (seconds, not minutes): the way to try a change before one full run. It samples by key values (e.g. item numbers) and keeps every row carrying one in every data source that has the key, so lookups still match; edge cases (blanks, errors, the largest values, lookup misses, declared strata) always appear. Pass sampling to choose or change it first (it is saved on the workbook: keyColumns, one {sourceId, column} per data source carrying the key; size key values; seed; strata columns; minPerStratum); without one and none saved, the result lists suggested keys traced from the workbook's lookups. Returns each worksheet's row count, first rows and failing validations, the sample's representativeness report (keys taken of all, how far blank and lookup match rates drift from the full data, warnings) and drift from the previous sample. Pass assertions to check pinned examples (these rows should show these values) on this sample only: they are never saved as validations. Sample results are never downloaded, reported or acted on; do one full run (run_open_workbook, mode 'entire') at the end. Call only when the user asks to run or test.",
    parameters: z.object({
      sampling: workbookSamplingSchema.optional(),
      assertions: z.array(sampleAssertionSchema).max(20).default([]),
      rowLimit: z.number().int().min(0).max(MAX_SAMPLE_ROWS).default(5),
    }),
    awaitable: true,
    execute: async (input) => {
      if (input.sampling) {
        if (!canUpdate)
          return {
            ok: false,
            error:
              "You can't change this workbook, so you can't set its sampling.",
          };
        const sampling = input.sampling;
        await applyAgentEdit(
          workbookId,
          port,
          null,
          (current) => ({ ...current, sampling }),
          [],
        );
      }
      const { definition } = port.snapshot();
      const refusal = sampleRunRefusal(
        definition,
        port.diagnostics(),
        port.plan(definition),
      );
      if (refusal) return refusal;
      const started = await port.run('sample', null);
      const run = await completedRun(workbookId, started.id);
      if (!run)
        return {
          ok: false,
          error:
            'The run on the sample is still going. Read workbook_runs for its outcome.',
        };
      const assertions = await Promise.all(
        input.assertions.map((assertion) =>
          checkAssertion(workbookId, run, definition, assertion),
        ),
      );
      return agentSampleRun(run, definition, input.rowLimit, assertions);
    },
  });
  const proposeTool = useFrontendTool({
    name: 'propose_worksheet_check',
    enabled: canUpdate,
    description:
      "Propose a relational check between two worksheets that stays true month to month, for the user to add or dismiss: every row's column value appears in another worksheet's column (no rows lost between them), or exactlyOnce appears there exactly once. It is a warning unless the user makes it an error. Only the user saves it: you can't add one with edit_workbook. Never propose fixed row counts, totals or example values: they go stale. Say why in reason (shown to the user).",
    parameters: z.object({
      sheetId: z.uuid().describe('The worksheet whose rows are checked.'),
      column: z.string().min(1),
      otherSheetId: z.uuid().describe('The worksheet they must appear in.'),
      matchColumn: z.string().min(1),
      exactlyOnce: z.boolean().default(false),
      reason: z.string().min(1).max(300),
    }),
    awaitable: true,
    execute: (input) => {
      const { definition } = port.snapshot();
      const sheet = definition.sheets.find((item) => item.id === input.sheetId);
      const other = definition.sheets.find(
        (item) => item.id === input.otherSheetId,
      );
      if (!sheet || !other || sheet.id === other.id)
        return {
          ok: false,
          error: 'Name two different worksheets of this workbook.',
        };
      const validation = workbookValidationSchema.parse({
        id: crypto.randomUUID(),
        column: input.column,
        kind: 'in-sheet',
        sheetId: other.id,
        matchColumn: input.matchColumn,
        exactlyOnce: input.exactlyOnce,
      });
      toast.info(
        input.exactlyOnce
          ? gt(
              'Check that every {column} of {sheet} appears exactly once in {other}?',
              {
                context: 'workbook agent proposed relational check',
                column: input.column,
                sheet: sheet.name,
                other: other.name,
              },
            )
          : gt('Check that every {column} of {sheet} appears in {other}?', {
              context: 'workbook agent proposed relational check',
              column: input.column,
              sheet: sheet.name,
              other: other.name,
            }),
        {
          description: input.reason,
          duration: Infinity,
          action: {
            label: gt('Add check', {
              context: 'workbook agent proposed relational check accept',
            }),
            onClick: () => {
              const latest = port.snapshot();
              port.applyIfUnchanged(
                latest.token,
                {
                  ...latest.definition,
                  sheets: latest.definition.sheets.map((item) =>
                    item.id === sheet.id
                      ? withValidation(item, validation)
                      : item,
                  ),
                },
                [],
              );
              void port.save();
            },
          },
        },
      );
      return {
        proposed: true,
        note: 'Shown to the user, who adds or dismisses it. Do not add it yourself.',
      };
    },
  });
  return [sampleTool, proposeTool];
}
